/**
 * Application Constants
 * Centralized configuration and constant values
 */

// API Configuration — Go routes are under /api (see main.go router.Group("/api"))
function normalizeApiBaseUrl(url) {
  const base = (url || 'http://localhost:8080').replace(/\/$/, '');
  return base.endsWith('/api') ? base : `${base}/api`;
}

export const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL);

const API_HOST = API_BASE_URL.replace(/\/api$/, '');

export const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) return "";
  if (imageUrl.startsWith('http')) return imageUrl;
  if (imageUrl.startsWith('/images/')) return `${API_HOST}${imageUrl}`;
  if (imageUrl.startsWith('/api/')) return `${API_HOST}${imageUrl.replace(/^\/api/, '')}`;
  return imageUrl.startsWith('/') ? `${API_HOST}${imageUrl}` : `${API_BASE_URL}/${imageUrl}`;
};

// Environment
export const IS_PRODUCTION = process.env.REACT_APP_ENV === 'production';
export const IS_DEVELOPMENT = process.env.REACT_APP_ENV === 'development';
export const IS_DEBUG = process.env.REACT_APP_DEBUG === 'true';

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/login',
  SIGNUP: '/signup',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  REFRESH_TOKEN: '/auth/refresh',
  
  // Client
  CLIENT_PROFILE: (clientId) => `/clients/${clientId}/my_profile`,
  CLIENT_DIET: (clientId) => `/clients/${clientId}/diet`,
  CLIENT_EXERCISE: (clientId) => `/clients/${clientId}/exercise`,
  CLIENT_RECIPES: (clientId) => `/clients/${clientId}/recipes`,
  CLIENT_MOTIVATION: (clientId) => `/clients/${clientId}/motivation`,
  CLIENT_WEIGHT: (clientId) => `/clients/${clientId}/weight_update`,
  
  // Admin
  ADMIN_CLIENTS: '/admin/clients',
  ADMIN_CLIENT_DETAILS: (clientId) => `/admin/clients/${clientId}`,
  ADMIN_DIET_TEMPLATES: '/admin/diet_templates',
  ADMIN_RECIPES: '/admin/recipes',
  ADMIN_RECIPE_NEW: '/admin/recipes/new',
  ADMIN_RECIPE: (recipeId) => `/admin/recipes/${recipeId}`,
  ADMIN_RECIPE_DELETE: (recipeId) => `/admin/recipes/${recipeId}/delete`,
  ADMIN_EXERCISES: '/admin/exercises',
  ADMIN_MOTIVATIONS: '/admin/motivations',
};

// User Types
export const USER_TYPES = {
  CLIENT: 'CLIENT',
  ADMIN: 'ADMIN',
};

// Diet Types
export const DIET_TYPES = {
  REGULAR: '1',
  DETOX: '2',
  DETOX_WATER: '3',
};

// Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  USER_TYPE: 'user_type',
  CLIENT_ID: 'client_id',
  EMAIL: 'email',
  IS_ACTIVE: 'is_active',
  USER: 'user',
  USER_ID: 'userId',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
};

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  ADMIN_LOGIN: '/admin/login',
  ADMIN_CLIENTS: '/admin/clients',
  ADMIN_DASHBOARD: '/admin/clients',
  CLIENT_DASHBOARD: (clientId) => `/clients/${clientId}/diet`,
  ACCOUNT_ACTIVATION: '/account-activation',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'Your session has expired. Please login again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  NOT_FOUND: 'Resource not found.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  PROFILE_UPDATED: 'Profile updated successfully!',
  PASSWORD_RESET: 'Password reset successfully!',
  DATA_SAVED: 'Data saved successfully!',
};

// Validation
export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 12,
  MAX_PASSWORD_LENGTH: 128,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  OTP_LENGTH: 6,
  PASSWORD_REQUIREMENTS: {
    UPPERCASE: /[A-Z]/,
    LOWERCASE: /[a-z]/,
    NUMBER: /[0-9]/,
    SPECIAL_CHAR: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/;'~`]/,
  },
  WEAK_PASSWORDS: [
    'password123!',
    'admin123456!',
    'welcome12345!',
    'qwerty123456!',
    '123456789abc!',
    'letmein12345!',
  ],
};

// Package duration in days (matches backend PackageDayMap)
export const PACKAGE_DURATION_DAYS = {
  '4 weeks': 28,
  '8 weeks': 56,
  '12 weeks': 84,
  '24 weeks': 168,
};

export const getPackageDurationDays = (packageName) => {
  if (!packageName) return null;
  const normalized = packageName.toLowerCase().replace(/\s+/g, ' ').trim();
  return PACKAGE_DURATION_DAYS[normalized] ?? null;
};

export const parseInputDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export const formatLocalDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Package start date: last payment date, or date of joining as fallback. */
export const getPackageStartDate = (client) =>
  client?.last_payment_date || client?.date_of_joining || '';

export const calculateNextPaymentDate = (packageStartDate, packageName) => {
  const days = getPackageDurationDays(packageName);
  const start =
    typeof packageStartDate === 'string'
      ? parseInputDate(packageStartDate)
      : packageStartDate;
  if (!days || !start || Number.isNaN(start.getTime())) return '';
  const next = new Date(start);
  next.setDate(next.getDate() + days);
  return formatLocalDate(next);
};

export const sortDietTemplatesByName = (templates) =>
  [...(templates || [])].sort((a, b) =>
    (a.Name || '').localeCompare(b.Name || '', undefined, { sensitivity: 'base' })
  );

export const withCalculatedNextPaymentDate = (client) => {
  const startDate = getPackageStartDate(client);
  if (!startDate || !client?.package) return client;
  return {
    ...client,
    next_payment_date: calculateNextPaymentDate(startDate, client.package),
  };
};

